import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button 
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import BasicInfoSection from './components/BasicInfoSection';
import PerformanceInfoSection from './components/PerformanceInfoSection';
import ComponentsSection from './components/ComponentsSection';
import PhysicalDataSection from './components/PhysicalDataSection';
import ElectricalSection from './components/ElectricalSection';
import ControlsSection from './components/ControlsSection';
import MiscellaneousSection from './components/MiscellaneousSection';
import AdditionalDocumentsSection from './components/AdditionalDocumentsSection';
import { HeatPumpForm } from './types/HeatPumpForm';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: 'Calibri, "Helvetica Neue", Arial, sans-serif',
    h4: {
      fontWeight: 'bold',
      fontSize: '1.4rem',
    },
    h5: {
      fontWeight: 'bold',
      fontSize: '1.2rem',
    },
    h6: {
      fontWeight: 'bold',
      fontSize: '1.1rem',
    },
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          margin: '10px 0',
        },
      },
    },
  },
});

const initialFormState: HeatPumpForm = {
  basicInfo: {
    ahgModel: '',
    supplier: '',
    supplierModel: '',
    refrigerantCode: '',
    source: 'Air',
    target: 'Water',
    ahgPrice: '',
    applications: '',
  },
  performanceInfo: {
    dhw: {
      capacity: '',
      cop: '',
      ambient: '7 (<25)',
      outlet: '65 (>59)',
      inlet: '15',
    },
    hhwHighTemp: {
      capacity: '',
      cop: '',
      ambient: '7 (<25)',
      outlet: '45/75 (>30)',
    },
    cooling: {
      capacity: '',
      cop: '',
      ambient: '35 (>25)',
      outlet: '7 (<15)',
    },
  },
  components: {
    compressor: {
      compressorType: '',
      inverterVfd: '',
      refrigerantCharge: '',
      oilType: '',
      oilCharge: '',
      compressorManufacturer: '',
      compressorPower: '',
      compressorQuantity: '',
    },
    waterSideHeatExchanger: {
      heatExchangerType: '',
      recommendedDeltaT: '',
      maxWorkingPressure: '',
      testPressure: '',
      maxWaterFlowRate: '',
      waterPressureDrop: '',
    },
    fanAirSideHeatExchange: {
      airSideHeatExchangerType: '',
      airDischargeType: '',
      fanInverterVfd: '',
      fanOutputPower: '',
      fanQuantity: '',
      totalAirflow: '',
    },
    waterPump: {
      manufacturer: '',
      model: '',
      powerConsumption: '',
      pressure: '',
      flowRate: '',
    },
  },
  physicalData: {
    dimensions: {
      height: '',
      length: '',
      width: '',
      dryWeight: '',
      operatingWeight: '',
    },
    clearances: {
      leftSide: '',
      rightSide: '',
      front: '',
      rear: '',
      top: '',
    },
  },
  electrical: {
    voltage: '',
    numberOfPhases: '',
    maxPowerInput: '',
    maxCurrent: '',
    frequency: '50',
  },
  controls: {
    lowLevel: {
      remoteStopStart: '',
      freeRunSignal: '',
      faultSignal: '',
    },
    highLevel: {
      modbus: '',
      bacnet: '',
      mqtt: '',
    },
  },
  miscellaneous: {
    operatingEnvelope: {
      minimumAmbient: '',
      maximumAmbient: '',
      maxOutletTemp: '',
    },
    connections: [''],
    soundPowerLevel: '',
  },
  additionalDocuments: {
    heatPumpImage: null,
    cadFiles: null,
    performanceData: null,
    acousticData: null,
    pricingData: null,
    manuals: null,
    wiringDiagrams: null,
    certifications: null,
  },
};

function App() {
  const [formData, setFormData] = useState<HeatPumpForm>(initialFormState);

  const handleFormChange = (section: string, subSection: string | null, field: string, value: any) => {
    setFormData(prevState => {
      const newState = { ...prevState };
      
      if (subSection) {
        // @ts-ignore
        newState[section][subSection][field] = value;
      } else {
        // @ts-ignore
        newState[section][field] = value;
      }
      
      return newState;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Form submitted:', formData);
    alert('Form submitted. Check console for data.');
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ padding: 3, marginTop: 3, marginBottom: 3 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              AHG information required from heat pump suppliers
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Draft 0.3 – 22.04.2025
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Ricky Blackman
            </Typography>
            <Typography variant="subtitle1" gutterBottom sx={{ fontStyle: 'italic' }}>
              Air to water heat pumps only
            </Typography>

            <BasicInfoSection 
              data={formData.basicInfo} 
              onChange={(field, value) => handleFormChange('basicInfo', null, field, value)} 
            />
            
            <PerformanceInfoSection 
              data={formData.performanceInfo}
              onChange={(subSection, field, value) => handleFormChange('performanceInfo', subSection, field, value)}
            />
            
            <ComponentsSection 
              data={formData.components}
              onChange={(subSection, field, value) => handleFormChange('components', subSection, field, value)}
            />
            
            <PhysicalDataSection 
              data={formData.physicalData}
              onChange={(subSection, field, value) => handleFormChange('physicalData', subSection, field, value)}
            />
            
            <ElectricalSection 
              data={formData.electrical}
              onChange={(field, value) => handleFormChange('electrical', null, field, value)}
            />
            
            <ControlsSection 
              data={formData.controls}
              onChange={(subSection, field, value) => handleFormChange('controls', subSection, field, value)}
            />
            
            <MiscellaneousSection 
              data={formData.miscellaneous}
              onChange={(subSection, field, value) => 
                subSection === 'connections' 
                  ? setFormData(prev => ({
                      ...prev, 
                      miscellaneous: {
                        ...prev.miscellaneous,
                        connections: value
                      }
                    }))
                  : handleFormChange('miscellaneous', subSection ? subSection : null, field, value)
              }
            />
            
            <AdditionalDocumentsSection 
              data={formData.additionalDocuments}
              onChange={(field, value) => handleFormChange('additionalDocuments', null, field, value)}
            />
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large"
              >
                Submit Form
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}

export default App;